import React, { FC } from "react";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

type LineDataType = {
  name: string;
  data: {
    x: string;
    y: number;
  }[];
  color?: string;
};

type PropsType = {
  title?: string;
  lines: LineDataType[];
  width?: number | string;
  height?: number;
  className?: string;
  xAxis: {
    title?: string;
    min?: number;
    max?: number;
  };
  yAxis: {
    title?: string;
    min?: number;
    max?: number;
  };
};

const generateColors = (count: number): string[] => {
  const baseColor = "#1A8CD8";
  if (count === 1) return [baseColor];

  const colors: string[] = [];
  const baseHue = 210; // #1A8CD8の色相
  const baseSaturation = 78; // #1A8CD8の彩度
  const baseLightness = 48; // #1A8CD8の明度

  for (let i = 0; i < count; i++) {
    if (i === 0) {
      // 1本目は必ず基準色を使用
      colors.push(baseColor);
    } else {
      // 2本目以降は色相を変化させる
      const hue = baseHue + (i * 360) / count;

      // 彩度と明度を少し調整して視認性を保つ
      const saturation = Math.max(
        60,
        Math.min(85, baseSaturation + (i % 2 === 0 ? 5 : -5)),
      );
      const lightness = Math.max(
        35,
        Math.min(60, baseLightness + (i % 3 === 0 ? 8 : i % 3 === 1 ? 0 : -8)),
      );

      colors.push(`hsl(${hue % 360}, ${saturation}%, ${lightness}%)`);
    }
  }

  return colors;
};

export const LineChart: FC<PropsType> = ({
  title,
  lines,
  width,
  height,
  className = "",
  xAxis,
  yAxis,
}) => {
  // カテゴリ（X軸の値）をすべての線から収集
  const allCategories = Array.from(
    new Set(lines.flatMap((line) => line.data.map((point) => point.x))),
  ).sort();

  // Highchartsのシリーズデータに変換
  const series = lines.map((line, index) => ({
    type: "line",
    name: line.name,
    data: allCategories.map((category) => {
      const point = line.data.find((p) => p.x === category);
      return point ? point.y : null;
    }),
    color: line.color || generateColors(lines.length)[index],
    lineWidth: 2,
    marker: {
      radius: 4,
    },
  }));

  const options: Highcharts.Options = {
    chart: {
      type: "line",
      height,
      width,
    },
    title: {
      text: title || "",
    },
    xAxis: {
      categories: allCategories,
      title: {
        text: xAxis.title || "",
      },
      min: xAxis.min,
      max: xAxis.max,
    },
    yAxis: {
      title: {
        text: yAxis.title || "",
      },
      min: yAxis.min || 0,
      max: yAxis.max,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: lines.length > 1,
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: true,
        connectNulls: false,
      },
    },
    series: series as Highcharts.SeriesOptionsType[],
  };

  return (
    <div
      className={`min-h-[250px] max-w-full flex justify-self-center ${className}`}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};
